import { useEffect, useMemo, useState } from "react";
import { trackButton, trackView, View as MetricView, Button } from "../services/metrics";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Image,
  ImageBackground,
  ActivityIndicator,
  StyleSheet,
  Platform,
  BackHandler,
} from "react-native";
import { colors, textStyle, backButtonStyle } from "../styles";
import { PAGE_PADDING, COLUMN_GAP, ROW_GAP } from "./Gallery.styles";
import Avatar from "./Avatar";
import { useESSStore, SharedComic } from "../store/essStore";
import { useSocketStore } from "../store/socketStore";
import { useNavigation } from "../navigation/useNavigation";

const sortIcon = require("../assets/icons/sort.png");
const backIcon = require("../assets/icons/caret-left.png");

/**
 * SharedGallery — the "Stories shared with you" screen (sharedGallery route,
 * /shared). Its own page now (was an in-gallery slide-over overlay); the route +
 * App-level cross-fade handle visibility/animation.
 */
export default function SharedGallery() {
  const { navigate, goBack } = useNavigation();
  const sharedComics = useESSStore((s) => s.sharedComics);
  const sharedComicsNext = useESSStore((s) => s.sharedComicsNextToken);
  const { listSharedComics, loadMoreSharedComics } = useSocketStore();
  const [loading, setLoading] = useState(true);
  const [sortByName, setSortByName] = useState(false);

  // Android hardware back + web Escape → back to wherever we came from.
  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => { if (e.key === "Escape") goBack(); };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }
    const sub = BackHandler.addEventListener("hardwareBackPress", () => { goBack(); return true; });
    return () => sub.remove();
  }, [goBack]);

  useEffect(() => {
    trackView(MetricView.SharedWithYou);
    setLoading(true);
    listSharedComics();
    const socket = useSocketStore.getState().socket;
    if (socket) {
      const done = () => setLoading(false);
      socket.once("sharedComicList", done);
      const timeout = setTimeout(done, 8000);
      return () => { socket.off("sharedComicList", done); clearTimeout(timeout); };
    } else {
      setLoading(false);
    }
  }, []);

  const sorted = useMemo(
    () => [...sharedComics].sort((a, b) =>
      sortByName
        ? (a.title || "").localeCompare(b.title || "")
        : b.sharedAt - a.sharedAt
    ),
    [sharedComics, sortByName],
  );

  return (
    <View style={s.container}>
      <View style={s.header}>
        <View style={s.headerLeft}>
          <Pressable style={s.backBtn} onPress={() => { trackButton(MetricView.SharedWithYou, Button.SharedWithYou.Close); goBack(); }} accessibilityLabel="Back" accessibilityRole="button">
            <Image source={backIcon} style={s.backIcon} resizeMode="contain" />
          </Pressable>
          <Text style={s.title}>Stories shared with you</Text>
        </View>
        <Pressable style={s.sortBtn} onPress={() => { const newSort = !sortByName; setSortByName(newSort); trackButton(MetricView.SharedWithYou, Button.SharedWithYou.SortToggle, newSort ? "asc" : "recent"); }}>
          <Image source={sortIcon} style={s.sortBtnIcon} resizeMode="contain" />
          <Text style={s.sortBtnText}>{sortByName ? "A / Z" : "New"}</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={s.loadingWrap}>
          <ActivityIndicator size="large" color={colors.textMuted} />
        </View>
      ) : sorted.length === 0 ? (
        <View style={s.loadingWrap}>
          <Text style={s.emptyText}>Nothing shared with you yet.</Text>
        </View>
      ) : (
        <ScrollView style={s.scrollView} contentContainerStyle={s.scrollContent}>
          <View style={s.grid}>
            {sorted.map((comic) => (
              <SharedComicCard
                key={comic.shareId}
                comic={comic}
                onPress={() => { trackButton(MetricView.SharedWithYou, Button.SharedWithYou.OpenComic); navigate("reader", { comic: comic.shareId, shared: true }); }}
              />
            ))}
          </View>
          {sharedComicsNext && (
            <Pressable
              style={s.loadMoreBtn}
              onPress={() => loadMoreSharedComics(sharedComicsNext)}
            >
              <Text style={s.loadMoreText}>Load more...</Text>
            </Pressable>
          )}
        </ScrollView>
      )}
    </View>
  );
}

function SharedComicCard({ comic, onPress }: { comic: SharedComic; onPress: () => void }) {
  return (
    <Pressable style={s.card} onPress={onPress}>
      {comic.coverImageUrl ? (
        <ImageBackground
          source={{ uri: comic.coverImageUrl }}
          style={s.cardImageWrap}
          imageStyle={s.cardImageBg}
          blurRadius={15}
        >
          <View style={s.cardImageOverlay} />
          <Image source={{ uri: comic.coverImageUrl }} style={s.cardImage} resizeMode="contain" />
        </ImageBackground>
      ) : (
        <View style={s.cardImageWrap}>
          <View style={s.cardPlaceholder}>
            <Text style={s.cardPlaceholderEmoji}>{"📖"}</Text>
          </View>
        </View>
      )}
      <View style={s.cardInfoRow}>
        <Avatar size={40} name={comic.senderName} uri={comic.senderAvatarUri} />
        <View style={s.cardTextWrap}>
          <Text style={s.cardTitle} numberOfLines={1}>{comic.title || "Untitled"}</Text>
          {comic.senderName && (
            <Text style={s.cardSender} numberOfLines={1}>{comic.senderName}</Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "rgba(38, 40, 66, 1)",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: PAGE_PADDING,
    paddingVertical: PAGE_PADDING,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  backBtn: backButtonStyle.container,
  backIcon: backButtonStyle.icon,
  title: {
    fontSize: 28,
    ...textStyle.headingMedium,
    color: "rgba(251, 248, 254, 1)",
  },
  sortBtn: {
    width: 121,
    // Match the back button's 48px height so the header row is the same height
    // as the gallery's (otherwise alignItems:center pushes the title/back down).
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.btnSecondary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  sortBtnIcon: {
    width: 20,
    height: 20,
    tintColor: colors.white,
  },
  sortBtnText: {
    ...textStyle.bodyMedium,
    fontSize: 14,
    color: colors.white,
  },
  loadingWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    ...textStyle.body,
    fontSize: 16,
    color: colors.textMuted,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: PAGE_PADDING,
    paddingBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: COLUMN_GAP,
    rowGap: ROW_GAP,
  },
  card: {
    width: 240,
    height: 296,
    borderRadius: 48,
    padding: 8,
    gap: 8,
    backgroundColor: "rgba(97, 74, 41, 1)",
  },
  cardImageWrap: {
    width: 224,
    height: 224,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  cardImageBg: {
    borderRadius: 40,
  },
  cardImageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.15)",
  },
  cardImage: {
    width: 110,
    height: 176,
  },
  cardPlaceholder: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  cardPlaceholderEmoji: {
    fontSize: 40,
  },
  cardInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    width: 224,
    height: 48,
    padding: 8,
    gap: 10,
  },
  cardTextWrap: {
    flex: 1,
  },
  cardTitle: {
    ...textStyle.bodyMedium,
    fontSize: 14,
    lineHeight: 21,
    color: "rgba(251, 248, 254, 1)",
  },
  cardSender: {
    ...textStyle.bodyMedium,
    fontSize: 12,
    lineHeight: 18,
    color: "rgba(159, 156, 198, 1)",
  },
  loadMoreBtn: {
    backgroundColor: colors.accent,
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
    marginTop: 20,
  },
  loadMoreText: {
    color: "#FFF",
    ...textStyle.heading,
    fontSize: 16,
  },
});
